/**
 * ThemeContext — global UI preferences for the Agent Dashboard.
 *
 * Holds:
 *   - theme ('light' | 'dark' | 'system')
 *   - resolvedTheme (actual applied mode after system resolution)
 *   - accentColor (primary highlight color: purple/blue/green/orange/pink/cyan)
 *   - hiddenPages (list of sidebar paths the user has hidden)
 *   - pageOrder (user-reordered list of sidebar paths)
 *
 * All values are persisted to localStorage and applied to
 * document.documentElement via CSS classes:
 *   - `dark` / `light` class toggles dark mode
 *   - `theme-<color>` class selects the active accent palette (see styles/index.css)
 *
 * The wider app reads these via the `useTheme()` hook. Matches the shape of
 * internal-planner's equivalent hook for easy cross-app component sharing.
 */
import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'

export type Theme = 'light' | 'dark' | 'system'
export type AccentColor = 'purple' | 'blue' | 'green' | 'orange' | 'pink' | 'cyan' | 'red' | 'black'

export const ACCENT_COLORS: AccentColor[] = [
  'purple', 'blue', 'green', 'orange', 'pink', 'cyan', 'red', 'black',
]

export const ACCENT_COLOR_STYLES: Record<AccentColor, { bg: string; label: string }> = {
  purple: { bg: 'bg-violet-500', label: 'Purple' },
  blue: { bg: 'bg-blue-500', label: 'Blue' },
  green: { bg: 'bg-emerald-500', label: 'Green' },
  orange: { bg: 'bg-orange-500', label: 'Orange' },
  pink: { bg: 'bg-pink-500', label: 'Pink' },
  cyan: { bg: 'bg-cyan-500', label: 'Cyan' },
  red: { bg: 'bg-red-500', label: 'Red' },
  black: { bg: 'bg-zinc-900', label: 'Black' },
}

interface ThemeContextType {
  // Theme mode
  theme: Theme
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => void

  // Accent color
  accentColor: AccentColor
  setAccentColor: (color: AccentColor) => void
  accentColors: AccentColor[]

  // Sidebar customization
  hiddenPages: string[]
  setHiddenPages: (paths: string[]) => void
  pageOrder: string[]
  setPageOrder: (order: string[]) => void
  resetSidebar: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const THEME_STORAGE_KEY = 'agent-dashboard-theme'
const ACCENT_STORAGE_KEY = 'agent-dashboard-accent'
const HIDDEN_PAGES_STORAGE_KEY = 'agent-dashboard-hidden-pages'
const PAGE_ORDER_STORAGE_KEY = 'agent-dashboard-page-order'

// ── Storage helpers ──

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system'
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  return 'system'
}

function getStoredAccent(): AccentColor {
  if (typeof window === 'undefined') return 'purple'
  const stored = localStorage.getItem(ACCENT_STORAGE_KEY) as AccentColor | null
  if (stored && ACCENT_COLORS.includes(stored)) return stored
  return 'purple'
}

function getStoredList(key: string): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []
  } catch {
    return []
  }
}

function applyAccentToDocument(accent: AccentColor) {
  const root = document.documentElement
  ACCENT_COLORS.forEach((c) => root.classList.remove(`theme-${c}`))
  root.classList.add(`theme-${accent}`)
}

// ── Provider ──

interface ThemeProviderProps {
  children: ReactNode
  defaultTheme?: Theme
}

export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme() || defaultTheme)
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    const t = getStoredTheme() || defaultTheme
    return t === 'system' ? getSystemTheme() : (t as 'light' | 'dark')
  })
  const [accentColor, setAccentColorState] = useState<AccentColor>(getStoredAccent)
  const [hiddenPages, setHiddenPagesState] = useState<string[]>(() =>
    getStoredList(HIDDEN_PAGES_STORAGE_KEY),
  )
  const [pageOrder, setPageOrderState] = useState<string[]>(() =>
    getStoredList(PAGE_ORDER_STORAGE_KEY),
  )

  // ── Apply theme mode (light/dark) to document ──
  useEffect(() => {
    const resolved = theme === 'system' ? getSystemTheme() : theme
    setResolvedTheme(resolved)

    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(resolved)

    localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [theme])

  // ── React to system theme changes when in 'system' mode ──
  useEffect(() => {
    if (theme !== 'system') return
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark' : 'light'
      setResolvedTheme(newTheme)
      document.documentElement.classList.remove('light', 'dark')
      document.documentElement.classList.add(newTheme)
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  // ── Apply accent color to document on mount + change ──
  useEffect(() => {
    applyAccentToDocument(accentColor)
    localStorage.setItem(ACCENT_STORAGE_KEY, accentColor)
  }, [accentColor])

  // ── Persist sidebar preferences ──
  useEffect(() => {
    localStorage.setItem(HIDDEN_PAGES_STORAGE_KEY, JSON.stringify(hiddenPages))
  }, [hiddenPages])
  useEffect(() => {
    localStorage.setItem(PAGE_ORDER_STORAGE_KEY, JSON.stringify(pageOrder))
  }, [pageOrder])

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme)
  }, [])

  const setAccentColor = useCallback((color: AccentColor) => {
    setAccentColorState(color)
  }, [])

  const setHiddenPages = useCallback((paths: string[]) => {
    setHiddenPagesState(paths)
  }, [])

  const setPageOrder = useCallback((order: string[]) => {
    setPageOrderState(order)
  }, [])

  const resetSidebar = useCallback(() => {
    setHiddenPagesState([])
    setPageOrderState([])
  }, [])

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        setTheme,
        accentColor,
        setAccentColor,
        accentColors: ACCENT_COLORS,
        hiddenPages,
        setHiddenPages,
        pageOrder,
        setPageOrder,
        resetSidebar,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
